/**
 * SubscriptionManage page component for managing active subscriptions.
 * 
 * This page allows users to view their current subscription details,
 * usage statistics, and manage their subscription (upgrade/downgrade/cancel).
 * 
 * Key Features:
 * - Displays current subscription plan and status
 * - Shows usage statistics (exams used/remaining)
 * - Allows subscription cancellation
 * - Shows billing period information
 * 
 * File Dependencies:
 * This file uses: React, Material-UI, utils/api
 * This file is used by: App.js routing
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Chip
} from '@mui/material';
import { 
  fetchSubscriptionStatus, 
  cancelSubscription,
  fetchSubscriptionPlans 
} from '../utils/api';
import analytics from '../services/analytics';

function SubscriptionManage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statusResponse, plansResponse] = await Promise.all([
        fetchSubscriptionStatus(),
        fetchSubscriptionPlans()
      ]);
      
      setStatus(statusResponse.data);
      setPlans(plansResponse.data);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (immediately = false) => {
    try {
      setCancelling(true);
      setError(null);
      
      // Track cancellation attempt
      analytics.trackSubscriptionCancelled(
        immediately ? 'immediate' : 'end_of_period',
        {
          plan_id: status?.plan_id,
          plan_name: status?.plan_name,
          current_period_end: status?.current_period_end,
          allowance_used: status?.allowance_used,
          exam_allowance: status?.exam_allowance
        }
      );
      
      await cancelSubscription(immediately);
      
      setSuccessMessage(
        immediately 
          ? 'Your subscription has been cancelled immediately.'
          : 'Your subscription will be cancelled at the end of the current billing period.'
      );
      setCancelDialogOpen(false);
      
      // Reload subscription status
      await loadData();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      // Handle specific error cases
      if (err.response?.status === 404) {
        // Check if subscription is already set to cancel
        if (status?.cancel_at_period_end) {
          setError('Your subscription is already set to cancel at the end of the billing period.');
        } else {
          setError('No active subscription found to cancel.');
        }
      } else {
        setError(err.response?.data?.detail || 'Failed to cancel subscription');
      }
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateUsagePercentage = () => {
    if (!status || status.exam_allowance === null) return 0;
    return (status.allowance_used / status.exam_allowance) * 100;
  };

  const getCurrentPlan = () => {
    if (!status || !plans.length) return null;
    // In a real implementation, you'd have the plan details in the status
    // For now, we'll just return a placeholder
    return plans[0]; // This should be matched by plan_id
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!status?.has_active_subscription && !status?.in_grace_period) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          You don't have an active subscription.
        </Alert>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/subscription/plans')}
        >
          View Subscription Plans
        </Button>
      </Container>
    );
  }

  const currentPlan = getCurrentPlan();

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Manage Subscription
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Current Plan Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Plan
              </Typography>
              <Box sx={{ mb: 2 }}>
                {status.in_grace_period ? (
                  <>
                    <Typography variant="h5" color="primary">
                      Free Trial
                    </Typography>
                    <Chip 
                      label={`${status.days_until_grace_expires} days remaining`}
                      color="warning"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="h5" color="primary">
                      {currentPlan?.plan_name || 'Active Plan'}
                    </Typography>
                    <Chip 
                      label={status.subscription_status}
                      color={status.subscription_status === 'active' ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Billing Period
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(status.current_period_start)} - {formatDate(status.current_period_end)}
              </Typography>
              
              {status.cancel_at_period_end && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Your subscription will end on {formatDate(status.current_period_end)}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Usage Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage This Period
              </Typography>
              
              {status.exam_allowance === null ? (
                <Box>
                  <Typography variant="h3" color="primary">
                    Unlimited
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You have used {status.allowance_used} exam{status.allowance_used !== 1 ? 's' : ''} this period
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" component="span" color="primary">
                      {status.allowance_used}
                    </Typography>
                    <Typography variant="h5" component="span" color="text.secondary">
                      {' '}/ {status.exam_allowance}
                    </Typography>
                  </Box>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateUsagePercentage()}
                    sx={{ mb: 1, height: 8, borderRadius: 4 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    {status.allowance_remaining} exam{status.allowance_remaining !== 1 ? 's' : ''} remaining
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Subscription Actions
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  // Track plan change intent
                  analytics.track('subscription_change_plan_clicked', {
                    current_plan_id: status?.plan_id,
                    current_plan_name: currentPlan?.plan_name,
                    subscription_status: status?.subscription_status,
                    cancel_at_period_end: status?.cancel_at_period_end
                  });
                  navigate('/subscription/plans');
                }}
                disabled={status.in_grace_period}
              >
                Change Plan
              </Button>
              
              {status.has_active_subscription && !status.cancel_at_period_end && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Cancel Subscription
                </Button>
              )}
              
              <Button
                variant="outlined"
                onClick={() => navigate('/generate')}
              >
                Generate Exam
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription?
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Cancel at Period End (Recommended):</strong> Keep access until {formatDate(status.current_period_end)}
            </Typography>
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Cancel Immediately:</strong> Lose access right away with no refund
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary">
            You can resubscribe anytime. No refunds will be issued for unused time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Keep Subscription
          </Button>
          <Button 
            onClick={() => handleCancelSubscription(false)} 
            color="warning"
            disabled={cancelling}
          >
            Cancel at Period End
          </Button>
          <Button 
            onClick={() => handleCancelSubscription(true)} 
            color="error"
            disabled={cancelling}
          >
            Cancel Immediately
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SubscriptionManage;